import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, ChevronRight, UserCircle } from 'lucide-react';
import { useListFeedbacks } from '@/hooks/usePerformance';
import { StatusBadge } from '@/components/performance/StatusBadge';
import { EpicTypeBadge } from '@/components/performance/EpicTypeBadge';
import { ScorePill } from '@/components/performance/ScorePill';
import { format } from 'date-fns';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export function PerformanceHistory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: feedbacks, isLoading } = useListFeedbacks();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = (feedbacks ?? []).filter((fb: any) => {
    const name = `${fb.clapperName} ${fb.clapperLastName}`.toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (typeFilter !== 'all' && fb.epicType !== typeFilter) return false;
    if (statusFilter !== 'all' && fb.status !== statusFilter) return false;
    return true;
  });

  function getAvgScore(dimensions: any[]): number | null {
    const scores = (dimensions ?? []).filter((d) => d.score !== null).map((d) => d.score as number);
    if (!scores.length) return null;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  }

  function getNavTarget(fb: any): string {
    if (fb.status === 'in_progress') return `/performance/epic/${fb.uuid}`;
    if (fb.epicType === 'feedback_session') return `/performance/epic/${fb.uuid}/feedback-result`;
    if (fb.epicType === 'performance') return `/performance/epic/${fb.uuid}/performance-review`;
    return `/performance/epic/${fb.uuid}/analysis`;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">{t('performance.history.title')}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{filtered.length} evaluaciones</p>
        </div>
        <Button size="sm" onClick={() => navigate('/performance')}>
          <Plus className="h-4 w-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">{t('performance.history.nueva_evaluacion')}</span>
          <span className="sm:hidden">Nueva</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <div className="relative flex-1 min-w-0 sm:min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('common.ui.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-full"
          />
        </div>
        <div className="flex gap-3 flex-wrap">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('performance.history.todos_los_tipos')}</SelectItem>
              <SelectItem value="seasonal">Seasonal</SelectItem>
              <SelectItem value="performance">Performance</SelectItem>
              <SelectItem value="feedback_session">{t('performance.history.feedback_session')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('performance.history.todos_los_estados')}</SelectItem>
              <SelectItem value="in_progress">{t('performance.history.en_curso')}</SelectItem>
              <SelectItem value="completed">Completada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <WinclapLoader size={32} className="text-foreground/25" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
            {t('performance.history.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((fb: any) => {
            const avgScore = getAvgScore(fb.dimensions ?? []);
            return (
              <button
                key={fb.uuid}
                onClick={() => navigate(getNavTarget(fb), { state: { from: '/performance/history' } })}
                className="w-full text-left rounded-lg border border-border hover:bg-accent transition-colors p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold shrink-0">
                    {fb.clapperName?.charAt(0)}{fb.clapperLastName?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{fb.clapperName} {fb.clapperLastName}</span>
                      <EpicTypeBadge type={fb.epicType ?? 'seasonal'} />
                      <StatusBadge status={fb.status ?? 'in_progress'} />
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-muted-foreground">{fb.impactLevel}</span>
                      {fb.date && (
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(fb.date), 'dd/MM/yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {fb.epicType !== 'performance' && fb.epicType !== 'feedback_session' && (
                      <ScorePill score={avgScore} />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/performance/clapper/${encodeURIComponent(fb.clapperEmail)}`);
                      }}
                      title={t('performance.history.view_profile')}
                      className="p-1 rounded hover:bg-accent transition-colors"
                    >
                      <UserCircle className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
